import { Component } from '@rhtml/component';
import { css, html, LitElement, property } from '@rxdi/lit-html';
import { Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { Droppable, ValidatedFile } from './droppable';

/**
 * @customElement file-uploader
 */
@Component({
  Settings: {
    selector: 'file-uploader',
    styles: [
      css`
        :host {
          outline: none;
          height: 100%;
          width: 100%;
          position: relative;
        }

        .overlay {
          pointer-events: none;
          display: none;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          /* background: rgba(255, 255, 255, 0.1); */
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          z-index: 1000;
          justify-content: center;
          align-items: center;
          border-radius: 13px;
        }

        :host(.dragover) .overlay {
          display: flex;
          animation: fadeIn 0.1s ease-in-out;
        }

        .drop-zone {
          padding: 3rem;
          border-radius: 16px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          color: var(--text-body, #333);
        }

        sl-icon {
          font-size: 3.5rem;
          color: var(--secondary, #3b82f6);
        }

        .text {
          font-size: 1.2rem;
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `,
    ],
  },
  State: function (this: FileUploaderComponent) {
    return this.onFilesDropped().pipe(startWith([]));
  },
  Render: () =>
    function (this) {
      return html`
        <slot></slot>
        <div class="overlay">
          <div class="drop-zone">
            <sl-icon name="cloud-upload"></sl-icon>
            <span class="text">Drop files to upload</span>
          </div>
        </div>
      `;
    },
})
export class FileUploaderComponent extends LitElement {
  private droppable: Droppable;

  @property({ type: Boolean })
  isClickable: boolean;

  @property({ type: Boolean })
  acceptsMultipleFiles = true;

  @property({ type: Array })
  acceptedFiles: string[] = [];

  @property({ type: Number })
  maxFileSize: number;

  @property({ type: Number })
  maxFiles: number;

  @property({ type: Number })
  filesCount = 0;

  @property({ type: Number })
  maxTotalFileSize: number;

  @property({ type: Number })
  filesTotalSize = 0;

  OnUpdateFirst() {
    this.droppable = new Droppable({
      element: this,
      acceptsMultipleFiles: this.acceptsMultipleFiles,
      isClickable: this.isClickable,
      acceptedFiles: this.acceptedFiles,
      maxFileSize: this.maxFileSize,
      maxFiles: this.maxFiles,
      filesCount: this.filesCount,
      maxTotalFileSize: this.maxTotalFileSize,
      filesTotalSize: this.filesTotalSize,
    });
    this.droppable.OnInit();

    this.dispatchEvent(new CustomEvent('init', { detail: this.droppable }));
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('filesCount')) {
      this.droppable.setFilesCount(this.filesCount);
    }
    if (changed.has('filesTotalSize')) {
      this.droppable.setFilesTotalSize(this.filesTotalSize);
    }
  }

  private onFilesDropped() {
    return new Observable<ValidatedFile[]>((observer) => {
      const listener = this.droppable.onFilesDropped((detail) => {
        this.dispatchEvent(new CustomEvent('filesDropped', { detail }));
        observer.next(detail);
      });

      return () => {
        observer.complete();
        listener();
      };
    });
  }

  OnDestroy() {
    this.droppable.destroy();
  }
}
